import type { FeatureFlagDefinitionEntryInput } from './feature-flags.constants';
import {
  ALL_SIGNALS_PAGE_ENABLED_FLAG,
  ADAPTIVE_SCANNING_ENABLED_FLAG,
  DEFER_AUTO_ATTACK_UNTIL_DISCOVERY_IDLE_FLAG,
  AUTO_PUBLISH_FINDINGS_FLAG,
  DEEP_SECURITY_TEST_CASE_SCAN_ENABLED_FLAG,
  LIVE_BROWSER_WORKSPACE_ENABLED_FLAG,
  BROWSER_SESSION_KUBERNETES_DRIVER_ENABLED_FLAG,
  BROWSER_SESSION_INDEXED_DB_ENABLED_FLAG,
  NEO_BROWSER_DISABLED_FLAG,
  DEEP_ACTIVE_PROBE_BROWSER_DISABLED_FLAG,
  DEEP_CANDIDATE_PROBE_ENABLED_FLAG,
  NEO_NAVIGATE_WEB_SESSION_INJECTION_ENABLED_FLAG,
  NEO_BROWSER_SESSION_TOOLS_ENABLED_FLAG,
  NEO_GENERIC_WORKER_ENABLED_FLAG,
  NEO_BROWSER_SESSION_AUTH_ENABLED_FLAG,
  NEO_POST_ATTACK_CONTEXT_VERIFICATION_ENABLED_FLAG,
  AUTHENTICATION_PREFLIGHT_ENABLED_FLAG,
  CREDENTIAL_LOGIN_LEASE_ENFORCEMENT_ENABLED_FLAG,
  CREDENTIAL_LOGIN_SESSION_VERIFICATION_ENABLED_FLAG,
  CREDENTIAL_LOGIN_SESSION_CONTINUITY_ENABLED_FLAG,
  NEO_RUN_LOGIN_ENABLED_FLAG,
  MFA_CODE_PLACEHOLDER_TYPING_ENABLED_FLAG,
  SECURITY_TEST_CASE_COMBINED_DUAL_INVOCATION_ENABLED_FLAG,
  SECURITY_TEST_CASE_DEPLOYMENT_SKILL_ENABLED_FLAG,
  SECURITY_TEST_CASE_AVAILABILITY_SKILL_ENABLED_FLAG,
  SECURITY_TEST_CASE_BUSINESS_CONTEXT_TOOLS_ENABLED_FLAG,
  SIGNAL_GENERATION_ATTRIBUTION_ENABLED_FLAG,
  INFORMATION_DISCLOSURE_SIGNALS_ENABLED_FLAG,
  SIGNAL_IMPORT_ENABLED_FLAG,
  SIGNALS_PAGE_ENABLED_FLAG,
  SIGNAL_IMPORT_PROBE_CONTEXT_TOOL_ENABLED_FLAG,
  SIGNAL_IMPORT_REPROCESS_UNCHANGED_ENABLED_FLAG,
  MISCONFIG_ENDPOINT_AUTO_SIGNALS_ENABLED,
  MISCONFIG_TECH_STACK_SIGNALS_ENABLED,
  SKIP_NOISE_URL_EXTENSIONS_FLAG,
  PROBE_DEFERRAL_FLUSH_MIN_CREATED_AT_DEFAULT_MS,
  WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES_FLAG,
  WEBSOCKET_TRACKING_ENABLED_FLAG,
  GRPC_WEB_SUPPORT_ENABLED_FLAG,
  AIRFLOW_FASTAPI_PLUGIN_ROUTE_HINTS_ENABLED_FLAG,
  PROTO_COLLECTION_MAX_FILE_COUNT_FLAG,
  DEFAULT_PROTO_COLLECTION_MAX_FILE_COUNT,
  DEFAULT_WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES,
  WEB_DISCOVERY_LIMIT_SCALE_FLAG,
  DEFAULT_WEB_DISCOVERY_LIMIT_SCALE,
  CLIENT_BUNDLE_SCAN_ENABLED_FLAG,
  BROWSER_HTTP_CACHE_ENABLED_FLAG,
  BROWSER_CONTEXT_IDLE_HOLD_ENABLED_FLAG,
  CLIENT_BUNDLE_SCAN_SECRETS_ENABLED_FLAG,
  CLIENT_BUNDLE_SCAN_SCA_ENABLED_FLAG,
  CLIENT_BUNDLE_SCAN_SAST_ENABLED_FLAG,
  CLIENT_BUNDLE_SCAN_NEO_VALIDATION_ENABLED_FLAG,
} from './feature-flags.constants';

export const DISCOVERY_FEATURE_FLAG_DEFINITIONS = [
  {
    name: 'XML_ENDPOINT_SPECIALIZED_CLASSIFICATION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'VOYAGER_AUTH_WITHOUT_FILTERS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'VOYAGER_AUTH_SKIP_POST_LOGIN_RELOAD_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: LIVE_BROWSER_WORKSPACE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_BROWSER_DISABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: DEEP_ACTIVE_PROBE_BROWSER_DISABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: BROWSER_SESSION_KUBERNETES_DRIVER_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: BROWSER_SESSION_INDEXED_DB_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'AUTH_HAR_DEBUG_PERSISTENCE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: WEBSOCKET_TRACKING_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: GRPC_WEB_SUPPORT_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AIRFLOW_FASTAPI_PLUGIN_ROUTE_HINTS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: PROTO_COLLECTION_MAX_FILE_COUNT_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_PROTO_COLLECTION_MAX_FILE_COUNT,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SKIP_NOISE_URL_EXTENSIONS_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_WEBSOCKET_HAR_MAX_MESSAGE_SAMPLES,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'PROBE_CANDIDATE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'ACTIVE_PROBE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'DEEP_ACTIVE_PROBE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: DEEP_CANDIDATE_PROBE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'SHARED_AGENT_KNOWLEDGE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'PROBE_DEFERRAL_FLUSH_MIN_CREATED_AT_MS',
    type: 'TIMESTAMP' as const,
    defaultValue: PROBE_DEFERRAL_FLUSH_MIN_CREATED_AT_DEFAULT_MS,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SIGNAL_IMPORT_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: SECURITY_TEST_CASE_COMBINED_DUAL_INVOCATION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SECURITY_TEST_CASE_DEPLOYMENT_SKILL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SECURITY_TEST_CASE_AVAILABILITY_SKILL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SECURITY_TEST_CASE_BUSINESS_CONTEXT_TOOLS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: DEEP_SECURITY_TEST_CASE_SCAN_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SIGNAL_GENERATION_ATTRIBUTION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: SIGNALS_PAGE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: ALL_SIGNALS_PAGE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'SIGNAL_IMPORT_SHADOW_PROMOTION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: SIGNAL_IMPORT_PROBE_CONTEXT_TOOL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SIGNAL_IMPORT_REPROCESS_UNCHANGED_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: ADAPTIVE_SCANNING_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: DEFER_AUTO_ATTACK_UNTIL_DISCOVERY_IDLE_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'CANDIDATE_PROBE_NEMOTRON_SUPER_120B_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CANDIDATE_PROBE_REACHABLE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'ACTIVE_PROBE_DESTRUCTIVE_METHODS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'NEO_CONFIRMED_TO_EXPLOITED_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: AUTO_PUBLISH_FINDINGS_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'NEO_POST_ATTACK_CODEBASE_TOOL_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_POST_ATTACK_CONTEXT_VERIFICATION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'NEO_INTELLIGENCE_EMAIL_INBOX_TOOL_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: NEO_NAVIGATE_WEB_SESSION_INJECTION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_BROWSER_SESSION_TOOLS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_GENERIC_WORKER_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_BROWSER_SESSION_AUTH_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTHENTICATION_PREFLIGHT_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CREDENTIAL_LOGIN_LEASE_ENFORCEMENT_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CREDENTIAL_LOGIN_SESSION_VERIFICATION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CREDENTIAL_LOGIN_SESSION_CONTINUITY_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: NEO_RUN_LOGIN_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: MFA_CODE_PLACEHOLDER_TYPING_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: 'NEO_STALE_TRACE_CLEANUP_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'NEO_ATTACK_RECURSION_LIMIT',
    type: 'NUMBER' as const,
    defaultValue: 0,
  },
  {
    name: 'NEO_ATTACK_RESUME_RECURSION_LIMIT',
    type: 'NUMBER' as const,
    defaultValue: 0,
  },
  {
    name: 'LICENSE_PAGE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: MISCONFIG_ENDPOINT_AUTO_SIGNALS_ENABLED,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: INFORMATION_DISCLOSURE_SIGNALS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: MISCONFIG_TECH_STACK_SIGNALS_ENABLED,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: WEB_DISCOVERY_LIMIT_SCALE_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_WEB_DISCOVERY_LIMIT_SCALE,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CLIENT_BUNDLE_SCAN_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: BROWSER_HTTP_CACHE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: BROWSER_CONTEXT_IDLE_HOLD_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CLIENT_BUNDLE_SCAN_SECRETS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CLIENT_BUNDLE_SCAN_SCA_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CLIENT_BUNDLE_SCAN_SAST_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: CLIENT_BUNDLE_SCAN_NEO_VALIDATION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
] as const satisfies readonly FeatureFlagDefinitionEntryInput[];
